import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { CARDS } from './cards.jsx';
import { MONSTERS, BACKDROPS } from './encounters.js';

const STORAGE_KEY = 'cybertech-combat-v3';
const ALL_IDS = CARDS.map((c) => c.id);

const pickDifferent = (count, current) => {
  if (count <= 1) return 0;
  let n = current;
  while (n === current) n = Math.floor(Math.random() * count);
  return n;
};

const load = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (raw && typeof raw.hp === 'number') {
      return {
        monster: raw.monster % MONSTERS.length || 0,
        bg: raw.bg % BACKDROPS.length || 0,
        hp: raw.hp,
        kills: raw.kills || 0,
        enabled: new Set(raw.enabled || ALL_IDS),
      };
    }
  } catch (e) {
    /* ignore */
  }
  return { monster: 0, bg: 0, hp: MONSTERS[0].maxHp, kills: 0, enabled: new Set(ALL_IDS) };
};

const CombatContext = createContext(null);

export const CombatProvider = ({ children }) => {
  const [state, setState] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          monster: state.monster,
          bg: state.bg,
          hp: state.hp,
          kills: state.kills,
          enabled: [...state.enabled],
        })
      );
    } catch (e) {
      /* ignore */
    }
  }, [state]);

  // Deal a card's damage to the current monster. Returns { dealt, killed }.
  const strike = useCallback((card) => {
    const dmg = card.damage || 0;
    let killed = false;
    setState((prev) => {
      const hp = Math.max(0, prev.hp - dmg);
      killed = hp <= 0;
      return { ...prev, hp };
    });
    return { dealt: dmg, killed };
  }, []);

  // Defeat the current monster → roll a new monster + backdrop (mixed) at full HP.
  const advance = useCallback(() => {
    setState((prev) => {
      const monster = pickDifferent(MONSTERS.length, prev.monster);
      const bg = pickDifferent(BACKDROPS.length, prev.bg);
      return { ...prev, monster, bg, hp: MONSTERS[monster].maxHp, kills: prev.kills + 1 };
    });
  }, []);

  const toggleCard = useCallback((id) => {
    setState((prev) => {
      const enabled = new Set(prev.enabled);
      if (enabled.has(id)) enabled.delete(id);
      else enabled.add(id);
      return { ...prev, enabled };
    });
  }, []);

  const monsterData = MONSTERS[state.monster] || MONSTERS[0];
  const maxHp = monsterData.maxHp;

  const value = {
    monster: state.monster,
    monsterData,
    bgUrl: BACKDROPS[state.bg] || BACKDROPS[0],
    hp: state.hp,
    maxHp,
    hpPct: maxHp > 0 ? Math.max(0, Math.round((state.hp / maxHp) * 100)) : 0,
    kills: state.kills,
    enabled: state.enabled,
    isEnabled: (id) => state.enabled.has(id),
    strike,
    advance,
    toggleCard,
  };

  return <CombatContext.Provider value={value}>{children}</CombatContext.Provider>;
};

export const useCombat = () => {
  const ctx = useContext(CombatContext);
  if (!ctx) throw new Error('useCombat must be used within CombatProvider');
  return ctx;
};
